import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { MessageSquare, ChevronRight } from "lucide-react";
import { fetchApi } from "@/lib/api";
import {
  AgentSdkThread,
  AgentSdkThreadsResponseSchema,
} from "@forgepoint/shared";
import { cn } from "@/lib/utils";
import { OpportunityThreadRow } from "./OpportunityThreadRow";

interface OpportunityThreadsSectionProps {
  opportunityId: string;
  primaryThreadId?: string | null;
  isEditing?: boolean;
  compact?: boolean;
  onThreadOpen?: (threadId: string) => void;
  children?: React.ReactNode;
}

/**
 * Keeps the primary conversation visible above the supplied content.
 * Other conversations are collapsed by default and ordered by recent activity.
 * Hidden when the opportunity is in edit mode.
 *
 * @param opportunityId - The ID of the opportunity to fetch threads for
 * @param primaryThreadId - The ID of the opportunity's main conversation
 * @param isEditing - Whether the opportunity is in edit mode. When true, the component is hidden.
 */
export const OpportunityThreadsSection: React.FC<
  OpportunityThreadsSectionProps
> = ({
  opportunityId,
  primaryThreadId = null,
  isEditing = false,
  compact = false,
  onThreadOpen,
  children,
}) => {
  const navigate = useNavigate();
  const [threads, setThreads] = useState<AgentSdkThread[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    let stale = false;
    setIsOpen(false);

    const fetchThreads = async () => {
      setIsLoading(true);
      try {
        const response = await fetchApi("standard-agent/threads", "GET", {
          params: {
            opportunityId,
          },
        });
        if (!stale && response.ok) {
          const parsed = AgentSdkThreadsResponseSchema.parse(response.data);
          setThreads(parsed.threads);
        }
      } finally {
        if (!stale) {
          setIsLoading(false);
        }
      }
    };

    fetchThreads();

    return () => {
      stale = true;
    };
  }, [opportunityId]);

  if (isEditing) {
    return null;
  }

  /**
   * Opens a standard agent thread from the opportunity page.
   * @param threadId - The ID of the thread to navigate to
   */
  const handleThreadClick = (threadId: string) => {
    if (onThreadOpen) {
      onThreadOpen(threadId);
      return;
    }
    navigate(`/standard-agent?threadId=${threadId}`);
  };

  const primaryThread = threads.find((thread) => thread.id === primaryThreadId);
  const otherThreads = threads
    .filter((thread) => thread.id !== primaryThreadId)
    .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());

  return (
    <div className={cn("space-y-5", !compact && "border-t pt-6")}>
      {isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-16 w-full rounded-lg" />
        </div>
      )}
      {!isLoading && primaryThread && (
        <section className="space-y-3" aria-label="Conversation">
          <h3 className={cn("font-semibold", compact ? "text-sm" : "text-lg")}>
            Conversation
          </h3>
          <div className="overflow-hidden rounded-lg border">
            <OpportunityThreadRow
              thread={primaryThread}
              onClick={handleThreadClick}
            />
          </div>
        </section>
      )}
      {children}
      {!isLoading && otherThreads.length > 0 && (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-sm py-1 text-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronRight
                className={cn(
                  "h-4 w-4 transition-transform motion-reduce:transition-none",
                  isOpen && "rotate-90",
                )}
              />
              <span>
                {primaryThread ? "Other conversations" : "Conversations"}
              </span>
              <Badge variant="secondary" className="text-xs tabular-nums">
                {otherThreads.length}
              </Badge>
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-3 overflow-hidden rounded-lg border divide-y">
              {otherThreads.map((thread) => (
                <OpportunityThreadRow
                  key={thread.id}
                  thread={thread}
                  onClick={handleThreadClick}
                />
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
      {!isLoading && threads.length === 0 && (
        <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
          <MessageSquare className="h-4 w-4 shrink-0" />
          No conversations linked yet
        </div>
      )}
    </div>
  );
};
